/**
 * Tempo point inspector (W2-06; DESIGN.md §4.11 "TEMPO POINT #2", §5.2): beat (the first point stays at beat 0),
 * BPM with the time it starts at, what later notes and keyframes do when the tempo changes (keep their beats, as
 * RPE, or keep their time: Core.tempo.setBpm policy, the project's EditorStoreState.tempoPolicy shared with Song &
 * Timing), delete, and a link to Song & Timing.
 * Several points: a common BPM field and delete (the beat-0 point is kept).
 */

import { useCallback, useMemo } from 'react';
import type { AppServices, InspectorSectionDef, InspectorSectionProps } from '../../../app/registry/types';
import { Core } from '../../../core/commands/index';
import type { TempoEditPolicy } from '../../../core/commands/types';
import type { BpmId, BpmPoint, ChartDoc } from '../../../core/model/types';
import { Beats } from '../../../core/time/beat';
import { useT } from '../../../i18n/useT';
import type { DocStore } from '../../../state/types';
import { BeatField, Button, Field, IconButton, NumberField, Section, Segmented } from '../../../ui';
import { useDocValue, useEditorValue } from '../hooks';
import { m } from '../inspector.messages';
import { arrayEq, fieldOr, sortedIds } from '../model';
import { Actions, fmtClock, Readout, Row, Title, useBeatCtx, useSectionBase, useStore } from '../parts';
import s from '../Inspector.module.css';
import { useEditKit } from '../tx';
import { FIELD, useFieldProblems } from '../useFieldProblems';

export function TempoSection(props: InspectorSectionProps) {
  const store = useStore(props.services);
  const ids = sortedIds(props.selection.bpm);
  if (store === null || ids.length === 0) return null;
  return <TempoBody services={props.services} store={store} ids={ids} />;
}

function TempoBody(props: {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly ids: readonly BpmId[];
}) {
  const { services, store } = props;
  const t = useT();
  const base = useSectionBase(services, store);
  const beatCtx = useBeatCtx(services, base);
  const policy = useEditorValue(services, (e) => e.tempoPolicy);
  const key = props.ids.join(',');
  const selector = useCallback(
    (doc: ChartDoc) =>
      (key === '' ? [] : key.split(',').map(Number))
        .map((id) => doc.bpm.get(id as BpmId))
        .filter((p): p is BpmPoint => p !== undefined)
        .sort((a, b) => Beats.cmp(a.t, b.t)),
    [key],
  );
  const points = useDocValue(store, selector, ['bpm'], arrayEq);
  const order = useDocValue(
    store,
    useCallback((doc: ChartDoc) => doc.bpm, []),
    ['bpm'],
  );
  const ids = useMemo(() => points.map((p) => p.id), [points]);
  const edit = useEditKit(services, store, `bpm:${ids.join(',')}`);
  const badges = useFieldProblems(services, {});
  const first = points[0];
  if (first === undefined) return null;
  const single = points.length === 1 ? first : null;
  const isZero = (p: BpmPoint): boolean => Beats.eq(p.t, Beats.ZERO);
  const index =
    single === null ? -1 : [...order.values()].sort((a, b) => Beats.cmp(a.t, b.t)).findIndex((p) => p.id === single.id);
  const deletable = points.filter((p) => !isZero(p)).map((p) => p.id);

  const report = (r: { readonly retimed: number; readonly snapped: number }): void => {
    if (r.retimed > 0)
      services.ui.toast({ key: m.tempoRetimed, params: { count: r.retimed, snapped: r.snapped } }, { kind: 'info' });
  };

  return (
    <Section
      title={
        <Title
          main={single === null ? t(m.temposTitle, { count: points.length }) : t(m.tempoTitle)}
          detail={single === null ? '' : `#${String(index)}`}
        />
      }
      actions={
        <IconButton
          icon="Trash2"
          size="sm"
          label={{ key: single === null ? m.deleteTempos : m.deleteTempo }}
          disabledReason={deletable.length === 0 ? { key: m.tempoBeatZero } : null}
          onClick={() => {
            edit.once({ key: m.txDeleteBpm }, (tx) => {
              for (const id of deletable) Core.tempo.deleteBpm(tx, { id, policy });
            });
          }}
        />
      }
    >
      <div className={s.fields}>
        {single !== null && (
          <Row edit={edit} field={FIELD.bpmBeat}>
            <BeatField
              label={t(m.tempoBeat)}
              value={{ kind: 'value', value: single.t }}
              ctx={beatCtx}
              echo
              disabled={isZero(single)}
              helpId={FIELD.bpmBeat}
              onCommit={(b) => {
                const out = edit.commit(FIELD.bpmBeat, { key: m.txBpm }, (tx) =>
                  Core.tempo.setBpm(tx, { id: single.id, t: b, policy }),
                );
                if (out.ok) report(out.value);
              }}
            />
            {isZero(single) && <Readout>{t(m.tempoBeatZero)}</Readout>}
          </Row>
        )}
        <Row edit={edit} field={FIELD.bpmValue}>
          <NumberField
            label={t(m.tempoBpm)}
            value={fieldOr(
              points.map((p) => p.bpm),
              120,
            )}
            step={1}
            min={0.001}
            precision={3}
            helpId={FIELD.bpmValue}
            badges={badges(FIELD.bpmValue)}
            onCommit={(v) => {
              if (v.kind !== 'number' || !(v.value > 0)) return;
              const bpm = v.value;
              const out = edit.commit(FIELD.bpmValue, { key: m.txBpm }, (tx) => {
                let retimed = 0;
                let snapped = 0;
                for (const id of ids) {
                  const r = Core.tempo.setBpm(tx, { id, bpm, policy });
                  retimed += r.retimed;
                  snapped += r.snapped;
                }
                return { retimed, snapped };
              });
              if (out.ok) report(out.value);
            }}
          />
          {single !== null && (
            <Readout testId="tempo-sec">{t(m.tempoSec, { sec: fmtClock(base.tempo.secAt(single.t)) })}</Readout>
          )}
        </Row>
        <Row edit={edit} field="bpm.policy">
          <Field label={t(m.tempoPolicy)} help={{ key: m.policyHelp }} helpId="bpm.policy">
            <Segmented<TempoEditPolicy>
              ariaLabel={t(m.tempoPolicy)}
              size="sm"
              value={policy}
              options={[
                { value: 'keepBeats', label: t(m.policyKeepBeats) },
                { value: 'keepTime', label: t(m.policyKeepTime) },
              ]}
              onChange={(p) => {
                services.editor.getState().setTempoPolicy(p);
              }}
            />
          </Field>
        </Row>
        <Actions>
          <Button
            size="sm"
            variant="ghost"
            icon="AudioWaveform"
            onClick={() => {
              void services.registry.run('app.workspace', { workspace: 'timing' });
            }}
          >
            {t(m.openTiming)}
          </Button>
        </Actions>
      </div>
    </Section>
  );
}

export const tempoSectionDef: InspectorSectionDef = {
  id: 'tempo',
  order: 400,
  level: 'guided',
  appliesTo: (sel) => sel.bpm.size > 0,
  component: TempoSection,
};
